import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { SearchCatalogKindEnum } from '@itsrever/rever-api'
import { useCatalogSearch } from '@/application/queries'
import { CatalogItem, CatalogKind } from '@/domain/catalog'
import { useDebounce } from '@/hooks/use-debounce'
import { RuleChips } from './rule-chips'

const {
  Card,
  FieldRoot,
  FieldLabel,
  FieldDescription,
  ComboboxRoot,
  ComboboxInputGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxItemDetail,
  ComboboxEmpty,
  ComboboxLoading,
  ComboboxCheckItem,
  ComboboxChevronTrigger,
  ComboboxDropdown,
  Thumbnail
} = Base

export type CatalogCopyKey =
  | 'products'
  | 'collections'
  | 'product_types'
  | 'product_tags'

interface CatalogPickerProps {
  ecommerceId?: string
  kind: CatalogKind
  copyKey: CatalogCopyKey
  value: string[]
  // Saved ids resolved against the store.
  known: Map<string, CatalogItem>
  staleIds: string[]
  onChange: (ids: string[]) => void
  canEdit: boolean
  // Where the copy of each list lives, for pickers outside Non-returnable items.
  copyPrefix?: string
  // Without its own card, for a picker inside another card.
  bare?: boolean
  // While the saved ids resolve: disabled, and no chips that would show bare ids.
  loading?: boolean
  // Loads the first options with the page, so the picker opens at once; disabled until they arrive.
  preload?: boolean
}

// Search only lists what exists in the store, so a picked rule always names a real id.
export const CatalogPicker: React.FC<CatalogPickerProps> = ({
  ecommerceId,
  kind,
  copyKey,
  value,
  known,
  staleIds,
  onChange,
  canEdit,
  copyPrefix = 'non_returnable',
  bare = false,
  loading = false,
  preload = false
}) => {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const debouncedQuery = useDebounce(query, 300) ?? ''
  const options = useCatalogSearch(
    ecommerceId,
    kind,
    debouncedQuery,
    open || preload
  )
  const { results, searching } = options
  const busy = loading || (preload && options.loading)
  const [picked, setPicked] = useState(new Map<string, CatalogItem>())

  const title = t(`${copyPrefix}.${copyKey}.title`)
  const hasThumbnails =
    kind === SearchCatalogKindEnum.Product ||
    kind === SearchCatalogKindEnum.Collection
  const selected = value.map(
    (id) => known.get(id) ?? picked.get(id) ?? placeholderItem(id)
  )

  const detailOf = (item: CatalogItem) => {
    if (kind === SearchCatalogKindEnum.Product) {
      return t('non_returnable.variants', { count: item.variantsCount })
    }
    if (kind === SearchCatalogKindEnum.Collection) {
      return t('non_returnable.products_count', { count: item.productsCount })
    }
    return undefined
  }

  const handleValueChange = (next: CatalogItem[]) => {
    setPicked(
      new Map([...picked, ...next.map((item) => [item.id, item] as const)])
    )
    onChange(next.map((item) => item.id))
  }

  const Wrapper = bare ? 'div' : Card
  return (
    <Wrapper
      className={bare ? 'flex flex-col gap-3' : 'flex flex-col gap-3 p-4'}
    >
      <FieldRoot>
        <FieldLabel>{title}</FieldLabel>
        <FieldDescription>
          {t(`${copyPrefix}.${copyKey}.description`)}
        </FieldDescription>
        <ComboboxRoot<CatalogItem, true>
          multiple
          items={results}
          value={selected}
          filter={null}
          inputValue={query}
          onInputValueChange={setQuery}
          onOpenChange={setOpen}
          disabled={!canEdit || busy}
          isItemEqualToValue={(item, other) => item.id === other.id}
          itemToStringLabel={(item) => item.label}
          onValueChange={handleValueChange}
        >
          <ComboboxInputGroup>
            <ComboboxInput
              placeholder={
                busy
                  ? t('non_returnable.loading')
                  : t(`${copyPrefix}.${copyKey}.placeholder`)
              }
            />
            <ComboboxChevronTrigger
              label={t('non_returnable.browse', { name: title })}
              loading={busy}
              loadingLabel={t('non_returnable.loading')}
            />
          </ComboboxInputGroup>
          <ComboboxDropdown>
            {searching ? (
              <ComboboxLoading label={t('non_returnable.searching')} />
            ) : (
              <ComboboxEmpty>{t('non_returnable.no_results')}</ComboboxEmpty>
            )}
            <ComboboxList>
              {(item: CatalogItem) => (
                <ComboboxCheckItem key={item.id} value={item}>
                  {hasThumbnails ? (
                    <Thumbnail
                      src={item.imageUrl || undefined}
                      alt={item.label}
                    />
                  ) : null}
                  <span>
                    {item.label}
                    {detailOf(item) ? (
                      <ComboboxItemDetail>{detailOf(item)}</ComboboxItemDetail>
                    ) : null}
                  </span>
                </ComboboxCheckItem>
              )}
            </ComboboxList>
          </ComboboxDropdown>
        </ComboboxRoot>
      </FieldRoot>
      {busy ? null : (
        <RuleChips
          chips={selected.map((item) => {
            const stale = staleIds.includes(item.id)
            return {
              id: item.id,
              label: item.label,
              description: stale
                ? t(`${copyPrefix}.${copyKey}.stale`)
                : detailOf(item),
              stale,
              thumbnail:
                hasThumbnails && !stale ? (
                  <Thumbnail
                    src={item.imageUrl || undefined}
                    alt={item.label}
                  />
                ) : undefined
            }
          })}
          emptyCopy={t(`${copyPrefix}.${copyKey}.empty`)}
          canEdit={canEdit}
          onRemove={(id) => onChange(value.filter((v) => v !== id))}
        />
      )}
    </Wrapper>
  )
}

function placeholderItem(id: string): CatalogItem {
  return {
    id,
    label: id,
    variantsCount: 0,
    productsCount: 0,
    imageUrl: ''
  }
}
